import { Button, Switch, Tooltip } from "@heroui/react";
import { Sheet } from "@heroui-pro/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { isAxiosError } from "axios";
import { CircleAlert, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { v4 } from "uuid";

import { AreaF, ComboF, MultiF, NumF, TextF } from "./fields";

import { FormError, ImageTile, notify } from "@/components/kit";
import {
  PRODUCT_SCHEMA,
  ProductFormData,
} from "@/components/product/modal/add-edit/schema";
import TagInput from "@/components/TagInput";
import { useGetAllBrands } from "@/hooks/api/brand/useGetAllBrands";
import { useGetAllCategories } from "@/hooks/api/category/useGetAllCategories";
import { useGetAllCountries } from "@/hooks/api/country/useGetAllCountries";
import { useGetAllSuppliers } from "@/hooks/api/supplier";
import { AddProduct, Product } from "@/types";

const DEFAULT_FORM_VALUES = {
  name: "",
  nameAr: "",
  externalId: "",
  description: "",
  deliveryNote: "",
  deliveryNoteAr: "",
  originalPrice: undefined,
  price: 0,
  priceOverride: false,
  // No market price until one is typed: it is optional, and a 0 here failed the positive-number rule.
  marketPrice: undefined,
  images: "",
  categoryIds: [] as string[],
  subCategoryIds: [] as string[],
  brandId: "",
  countryIds: [] as string[],
  tags: [],
  position: 0,
  supplierId: "",
};

interface AddProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  product?: Partial<Product> | null;
  isImport?: boolean;
  onSave: (product: AddProduct) => void;
  isLoading: boolean;
  isError: boolean;
  resetMutation: () => void;
  error: Error | null;
}

function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-col gap-0.5">
        <h3 className="text-sm font-semibold">{title}</h3>
        {subtitle ? <p className="text-muted text-xs">{subtitle}</p> : null}
      </div>
      {children}
    </section>
  );
}

function ImagePreview({
  url,
  onRemove,
  isDisabled,
}: {
  url: string;
  onRemove: () => void;
  isDisabled?: boolean;
}) {
  return (
    <div className="group relative">
      <ImageTile size="lg" src={url} />
      {!isDisabled && (
        <button
          aria-label="Remove image"
          className="bg-(--danger) absolute -end-2 -top-2 flex size-5 cursor-pointer items-center justify-center rounded-full text-white opacity-0 shadow-sm outline-none transition-opacity focus-visible:opacity-100 group-hover:opacity-100"
          type="button"
          onClick={onRemove}
        >
          <X className="size-3" />
        </button>
      )}
    </div>
  );
}

export default function ProductFormModal({
  isOpen,
  onClose,
  product,
  onSave,
  isLoading,
  isImport,
  error,
  isError,
  resetMutation,
}: AddProductModalProps) {
  const { data: categories, isLoading: isCategoriesLoading } =
    useGetAllCategories();
  const { data: brands, isLoading: isBrandsLoading } = useGetAllBrands();
  const { data: countries, isLoading: isCountriesLoading } =
    useGetAllCountries();
  const { data: suppliers, isLoading: isSuppliersLoading } =
    useGetAllSuppliers();
  const [tags, setTags] = useState<string[]>([]);

  const { handleSubmit, reset, setValue, watch, control } =
    useForm<ProductFormData>({
      resolver: zodResolver(PRODUCT_SCHEMA),
      defaultValues: DEFAULT_FORM_VALUES,
    });

  const isEditMode = Boolean(product) && !isImport;
  const isImportMode = isImport;

  const watchedPriceOverride = watch("priceOverride");
  const watchedCategories = watch("categoryIds");
  const watchedImages = watch("images");
  const watchedSubCategoryIds = watch("subCategoryIds");

  const availableSubCategories = useMemo(() => {
    if (!categories || watchedCategories.length === 0) return [];
    const subs = categories
      .filter((c) => watchedCategories.includes(c.id))
      .flatMap((c) => c.subCategories);

    // Deduplicate: a subcategory can appear under multiple categories in M2M
    return [...new Map(subs.map((s) => [s.id, s])).values()];
  }, [categories, watchedCategories]);

  // Auto-prune subcategories that no longer belong to any selected category
  useEffect(() => {
    const validIds = new Set(availableSubCategories.map((sc) => sc.id));
    const current = watchedSubCategoryIds;
    const pruned = current.filter((id) => validIds.has(id));

    if (pruned.length !== current.length) {
      setValue("subCategoryIds", pruned);
    }
  }, [availableSubCategories]);

  const imageUrls = useMemo(() => {
    if (!watchedImages) return [];

    return watchedImages
      .split(",")
      .map((url) => url.trim())
      .filter((url) => url.length > 0);
  }, [watchedImages]);

  const removeImage = (indexToRemove: number) => {
    const newUrls = imageUrls.filter((_, index) => index !== indexToRemove);

    setValue("images", newUrls.join(", "));
  };

  const untouched = (key: "deliveryNote" | "deliveryNoteAr" | "position") =>
    Boolean(product) && product?.[key] === undefined;

  const onSubmitForm = (values: ProductFormData) => {
    // Cross-validate: every subcategory must belong to at least one selected category
    const validSubCatIds = new Set(
      categories
        ?.filter((c) => values.categoryIds.includes(c.id))
        .flatMap((c) => c.subCategories.map((sc) => sc.id)) ?? [],
    );
    const invalidSubs = values.subCategoryIds.filter(
      (id) => !validSubCatIds.has(id),
    );

    if (invalidSubs.length > 0) {
      notify.error(
        "Invalid subcategory selection",
        "Some subcategories don't belong to any of the selected categories. Please fix your selection.",
      );

      return;
    }

    const productData: AddProduct = {
      ...values,
      originalPrice: values.originalPrice,
      priceOverride: values.priceOverride,
      images: values.images
        .split(",")
        .map((url: string) => url.trim())
        .filter((url: string) => url),
      tags: tags,
      externalId: values.externalId,
      description: values.description,
      descriptionAr: values.descriptionAr,
      // A field the incoming product does not have stays out of the request, as when its section was collapsed.
      deliveryNote: untouched("deliveryNote") ? undefined : values.deliveryNote,
      deliveryNoteAr: untouched("deliveryNoteAr")
        ? undefined
        : values.deliveryNoteAr,
      categoryIds: values.categoryIds,
      subCategoryIds: values.subCategoryIds,
      brandId: values.brandId,
      countryIds: values.countryIds,
      position: untouched("position") ? undefined : values.position,
      supplierId: values.supplierId,
      ...(values.marketPrice ? { marketPrice: values.marketPrice } : {}),
      ...(values.priceOverride && values.price ? { price: values.price } : {}),
    };

    onSave(productData);
  };

  const handleClose = () => {
    reset();
    setTags([]);
    onClose();
    resetMutation();
  };

  const resetFormWithProduct = (product: Partial<Product>) => {
    reset({
      name: product.name,
      nameAr: product.nameAr,
      externalId: product.externalId,
      description: product.description,
      descriptionAr: product.descriptionAr,
      deliveryNote: product.deliveryNote,
      deliveryNoteAr: product.deliveryNoteAr,
      originalPrice: product.originalPrice,
      price: product.price,
      priceOverride: product.priceOverride || false,
      marketPrice: product.marketPrice,
      images: product.images?.join(", "),
      categoryIds:
        product.categories?.map((c) => c.id) ??
        (product.category ? [product.category.id] : []),
      subCategoryIds:
        product.subCategories?.map((sc) => sc.id) ??
        (product.subCategory ? [product.subCategory.id] : []),
      brandId: product.brand?.id,
      countryIds:
        product.countries?.map((c) => c.id) ??
        (product.country ? [product.country.id] : []),
      tags: product.tags || [],
      position: product.position,
      supplierId: product.supplier?.id,
    });
    setTags(product.tags ?? []);
  };

  useEffect(() => {
    if (product) {
      resetFormWithProduct(product);
    } else {
      reset({ ...DEFAULT_FORM_VALUES, externalId: v4() });
    }
  }, [product?.id, reset, setValue, isOpen]);

  const title = isEditMode
    ? "Edit Product"
    : isImportMode
      ? "Import Product"
      : "Add New Product";
  const submitLabel = isLoading
    ? isImportMode
      ? "Importing..."
      : isEditMode
        ? "Saving..."
        : "Adding..."
    : isImportMode
      ? "Import Product"
      : isEditMode
        ? "Save Changes"
        : "Add Product";
  const errorMessage = isError
    ? `${
        isImportMode
          ? "Failed to import product"
          : isEditMode
            ? "Failed to edit product"
            : "Failed to add product"
      }: ${
        isAxiosError(error)
          ? JSON.stringify(error.response?.data)
          : JSON.stringify(error)
      }`
    : null;

  return (
    <Sheet
      isDetached
      isDismissable={false}
      isOpen={isOpen}
      placement="right"
      onOpenChange={(open) => !open && handleClose()}
    >
      <Sheet.Backdrop className="z-0">
        <Sheet.Content className="h-[calc(100dvh-1rem)] w-full max-w-lg">
          <Sheet.Dialog>
            <form
              noValidate
              className="flex h-full min-h-0 flex-col"
              onSubmit={(event) => {
                event.preventDefault();
                handleSubmit(onSubmitForm)();
              }}
            >
              <Sheet.CloseTrigger />
              <Sheet.Header>
                <Sheet.Heading>{title}</Sheet.Heading>
                <p className="text-muted text-sm">
                  {isEditMode
                    ? `Editing: ${product?.name}`
                    : "Fill in the product details below"}
                </p>
              </Sheet.Header>
              <Sheet.Body className="flex min-h-0 flex-1 flex-col gap-8 overflow-y-auto">
                <FormError message={errorMessage} />

                <Section
                  subtitle="Product name, supplier, and identifier"
                  title="Basic Information"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <TextF
                      isRequired
                      control={control}
                      isDisabled={isLoading}
                      label="Product Name (English)"
                      name="name"
                      placeholder="Enter product name"
                    />
                    <TextF
                      isRequired
                      control={control}
                      dir="rtl"
                      isDisabled={isLoading}
                      label="Product Name (Arabic)"
                      name="nameAr"
                      placeholder="ادخل اسم المنتج"
                    />
                    <TextF
                      isMono
                      isRequired
                      control={control}
                      isDisabled={isLoading || Boolean(product)}
                      label="External ID / SKU"
                      name="externalId"
                      placeholder="External ID"
                    />
                    <ComboF
                      isRequired
                      control={control}
                      empty="No suppliers available"
                      isDisabled={isLoading || isSuppliersLoading}
                      label="Supplier"
                      name="supplierId"
                      options={suppliers ?? []}
                      placeholder="Select a supplier"
                    />
                  </div>
                </Section>

                <Section
                  subtitle="Category, brand, and country of origin"
                  title="Classification"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <MultiF
                      isRequired
                      control={control}
                      isDisabled={isLoading || isCategoriesLoading}
                      label="Categories"
                      name="categoryIds"
                      options={categories ?? []}
                      placeholder="Select categories"
                    />
                    <MultiF
                      isRequired
                      control={control}
                      groups={(categories ?? [])
                        .filter((c) => watchedCategories.includes(c.id))
                        .map((category) => ({
                          id: category.id,
                          title: category.name,
                          options: category.subCategories,
                        }))}
                      isDisabled={isLoading || watchedCategories.length === 0}
                      label="Sub Categories"
                      name="subCategoryIds"
                      placeholder={
                        watchedCategories.length > 0
                          ? "Select sub categories"
                          : "Select categories first"
                      }
                    />
                    <ComboF
                      isRequired
                      control={control}
                      empty="No brands available"
                      isDisabled={isLoading || isBrandsLoading}
                      label="Brand"
                      name="brandId"
                      options={brands ?? []}
                      placeholder="Select a brand"
                    />
                    <MultiF
                      isRequired
                      control={control}
                      isDisabled={isLoading || isCountriesLoading}
                      label="Countries of Origin"
                      name="countryIds"
                      options={countries ?? []}
                      placeholder="Select countries"
                    />
                  </div>
                </Section>

                <Section
                  subtitle="Product pricing and position"
                  title="Pricing"
                >
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    <NumF
                      isRequired
                      control={control}
                      isDisabled={isLoading}
                      label="Original Price"
                      name="originalPrice"
                      placeholder="0.00"
                      prefix="AED"
                    />
                    <NumF
                      control={control}
                      isDisabled={isLoading}
                      label="Market Price"
                      name="marketPrice"
                      placeholder="0.00"
                      prefix="AED"
                    />
                    <NumF
                      control={control}
                      isDisabled={isLoading}
                      label="Display Position"
                      name="position"
                      placeholder="0"
                    />
                  </div>

                  <div className="flex flex-col gap-3">
                    <Controller
                      control={control}
                      name="priceOverride"
                      render={({ field }) => (
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">
                              Override Price
                            </span>
                            <Tooltip delay={300}>
                              <CircleAlert
                                aria-label="About overriding the price"
                                className="text-muted size-3.5 cursor-help"
                                tabIndex={0}
                              />
                              <Tooltip.Content>
                                Enable to set a custom selling price different
                                from the calculated price
                              </Tooltip.Content>
                            </Tooltip>
                          </div>
                          <Switch
                            aria-label="Override Price"
                            isDisabled={isLoading}
                            isSelected={field.value}
                            size="sm"
                            onChange={field.onChange}
                          >
                            <Switch.Content>
                              <Switch.Control>
                                <Switch.Thumb />
                              </Switch.Control>
                            </Switch.Content>
                          </Switch>
                        </div>
                      )}
                    />
                    {watchedPriceOverride && (
                      <NumF
                        isRequired
                        control={control}
                        isDisabled={isLoading}
                        label="Custom Selling Price"
                        name="price"
                        placeholder="0.00"
                        prefix="AED"
                      />
                    )}
                  </div>
                </Section>

                <Section
                  subtitle="Add product photos and media"
                  title="Product Images"
                >
                  <AreaF
                    isMono
                    isRequired
                    control={control}
                    description="Enter multiple image URLs separated by commas"
                    isDisabled={isLoading}
                    label="Image URLs"
                    name="images"
                    placeholder="https://example.com/image1.jpg, https://example.com/image2.jpg"
                    rows={2}
                  />
                  {imageUrls.length > 0 && (
                    <div className="flex flex-col gap-2">
                      <p className="text-muted text-xs">
                        Preview ({imageUrls.length} image
                        {imageUrls.length > 1 ? "s" : ""})
                      </p>
                      <div className="flex flex-wrap gap-3">
                        {imageUrls.map((url, index) => (
                          <ImagePreview
                            key={`${url}-${index}`}
                            isDisabled={isLoading}
                            url={url}
                            onRemove={() => removeImage(index)}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </Section>

                <Section
                  subtitle="Description, delivery notes, and tags (optional)"
                  title="Additional Details"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <AreaF
                      control={control}
                      isDisabled={isLoading}
                      label="Description (English)"
                      name="description"
                      placeholder="Enter product description"
                    />
                    <AreaF
                      control={control}
                      dir="rtl"
                      isDisabled={isLoading}
                      label="Description (Arabic)"
                      name="descriptionAr"
                      placeholder="ادخل وصف المنتج"
                    />
                    <AreaF
                      control={control}
                      isDisabled={isLoading}
                      label="Delivery Note (English)"
                      name="deliveryNote"
                      placeholder="Enter delivery note"
                      rows={2}
                    />
                    <AreaF
                      control={control}
                      dir="rtl"
                      isDisabled={isLoading}
                      label="Delivery Note (Arabic)"
                      name="deliveryNoteAr"
                      placeholder="ادخل ملاحظة التوصيل"
                      rows={2}
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <span className="text-sm font-medium">Tags</span>
                    <TagInput
                      disabled={isLoading}
                      placeholder="Type a tag and press Enter"
                      tags={tags}
                      onChange={(newTags) => {
                        setTags(newTags);
                        setValue("tags", newTags);
                      }}
                    />
                  </div>
                </Section>
              </Sheet.Body>
              <Sheet.Footer className="flex items-center justify-between">
                <div>
                  {isEditMode && (
                    <Button
                      size="sm"
                      type="button"
                      variant="ghost"
                      onPress={() => resetFormWithProduct(product!)}
                    >
                      Reset Changes
                    </Button>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    isDisabled={isLoading}
                    type="button"
                    variant="ghost"
                    onPress={handleClose}
                  >
                    Cancel
                  </Button>
                  <Button
                    isDisabled={isLoading}
                    isPending={isLoading}
                    type="submit"
                  >
                    {submitLabel}
                  </Button>
                </div>
              </Sheet.Footer>
            </form>
          </Sheet.Dialog>
        </Sheet.Content>
      </Sheet.Backdrop>
    </Sheet>
  );
}
